/**
 * Vite plugin for RUN.world playground mode.
 *
 * This plugin enables H5 apps to communicate with the real Firebase backend
 * during local development. It:
 * 1. Reads game.config.{env}.json for gameId
 * 2. Injects Firebase config into the page
 *
 * Authentication (dev/staging): Google Sign-In via the playground toolbar.
 *
 * Authentication (local): Email/password against the Auth emulator via toolbar.
 *
 * Usage:
 * ```typescript
 * // vite.config.ts
 * import { defineConfig } from 'vite'
 * import { rundotGamePlaygroundPlugin } from '@series-inc/rundot-game-sdk/vite'
 *
 * export default defineConfig({
 *   plugins: [
 *     rundotGamePlaygroundPlugin(),
 *   ],
 * })
 * ```
 */

import type { Plugin, IndexHtmlTransformResult, HtmlTagDescriptor } from 'vite'
import { loadEnv } from 'vite'
import { createHash } from 'crypto'
import { readFileSync, writeFileSync } from 'fs'
import { join, dirname, resolve as resolvePath } from 'path'
import type { IncomingMessage, ServerResponse } from 'http'
import { mergeGameConfigs } from './configMerger'
import { RUNDOT_LOOKUP_ORDER } from './rundotPaths'
import { isServerConfigChange } from './serverConfigWatch'
import { createPlaygroundToolbarTags } from './playgroundToolbar'
import { buildRevenueCatImportMapTag } from './revenueCatImportMap'
import { isSafeConfigTarget, isSafeGameId, isLoopbackHost, isProtectedRundotPath, isPlaygroundRequestAllowed, shouldExposeHeadlessAuth, shouldAllowLanAnonymousAuth } from './devServerSecurity'
import { findUpGameConfigPath, resolveGameConfigWritePath, resolveSuggestedGameName } from './gameConfigPath'
import { FIREBASE_CONFIGS, FUNCTIONS_REGIONS, DEFAULT_EMULATOR_HOSTS, CLOUD_RUN_URLS, GAME_MANAGER_URLS, REVENUECAT_WEB_BILLING_KEYS, ROOM_SERVER_URLS } from '../firebase/firebaseConfigs'
import type { PlaygroundTarget, PlaygroundConfig } from '../config/playground'
import { fileURLToPath } from 'url'
import { RUNDOT_HEADLESS_AUTH_PATH, handleHeadlessAuth } from './headlessAuth'

/** Custom header the SDK's own browser fetches send so the dev-server CSRF guard
 *  can distinguish them from a cross-origin simple request. A cross-origin page
 *  cannot set this header without triggering a CORS preflight the server never approves. */
const PLAYGROUND_REQUEST_HEADER = 'x-rundot-playground'

/** Env opt-in to allow a non-`pk_` (internal QA) headless key — bot/CI only. */
const ALLOW_NON_RK_KEY_ENV = 'RUNDOT_ALLOW_NON_RK_KEY'

/** Env opt-out of the headless-key + non-loopback-bind fail-fast guard. */
const ALLOW_INSECURE_KEY_BIND_ENV = 'RUNDOT_ALLOW_INSECURE_KEY_BIND'

const SDK_VERSION: string = (() => {
  try {
    const pkgPath = join(dirname(fileURLToPath(import.meta.url)), '..', '..', 'package.json')
    const pkg = JSON.parse(readFileSync(pkgPath, 'utf-8')) as { version: string }
    return pkg.version
  } catch {
    return 'unknown'
  }
})()

// Serve the playground entry point as an external script via a dev-server middleware
// endpoint instead of an inline <script> block. Vite only resolves bare import
// specifiers in external script sources — inline <script> blocks are emitted
// verbatim and will cause a "Failed to resolve module specifier" error in the browser.
const PLAYGROUND_INIT_PATH = '/@rundot-game-sandbox-init.js'

// IMPORTANT:
// Playground mode relies on Firebase's modular v9+ API. This SDK repo currently uses
// Firebase 12.x (see tests/test-app and package-lock), so the install hint must
// match 12.x to avoid users installing 11.x by mistake due to semver ranges.
const REQUIRED_FIREBASE_VERSION = '^12.7.0'

// RevenueCat Web Billing is only needed for IAP / subscription flows in playground.
// Version mirrors admin/package.json so creators end up on the same RC SDK we
// validate the rest of the stack against.
const REQUIRED_REVENUECAT_VERSION = '^1.39.1'

export interface RundotGamePlaygroundPluginOptions {
  /** Override the target environment (default: 'playground') */
  target?: PlaygroundTarget
  /** Game/App ID - required if game.config.{env}.json doesn't exist */
  gameId?: string
  /** Disable playground even if configured */
  disabled?: boolean
  /**
   * Headless dev-server key for playground sign-in (bypasses UI-based login).
   * Normally you do NOT set this here — leave it out and let the plugin read
   * `RUNDOT_PLAYGROUND_KEY` from `.env` / `.env.local` (minted by
   * `rundot playground grant-access`).
   *
   * SECURITY: this key is dev-server-side ONLY. It is never serialized to the
   * browser — the SDK gets a short-lived custom token from a localhost-only
   * dev-server middleware instead. Use a playground (`pk_`) key from
   * `rundot playground grant-access`; an `rk_` deploy key is ignored and a
   * non-`pk_` (internal QA) key is rejected unless `RUNDOT_ALLOW_NON_RK_KEY=1`
   * is set. The dev server must stay bound to localhost. It is intentionally
   * distinct from the deploy key (`RUNDOT_API_KEY`), which the plugin never reads.
   *
   * Resolution order: this option → `process.env.RUNDOT_PLAYGROUND_KEY` →
   * project `.env*` `RUNDOT_PLAYGROUND_KEY`.
   */
  apiKey?: string
  /** Override the app role returned by app.getMyRole() in playground mode.
   *  Set to 'owner' or 'editor' to test admin flows locally. */
  appRole?: 'owner' | 'editor' | 'none'
}

const GAME_CONFIG_ENV_FILENAME = (env: string): string => `game.config.${env}.json`

/**
 * Description of a playground dependency the project is missing. Drives both the
 * console log and the browser overlay.
 */
type MissingDep = {
  packageName: string
  versionConstraint: string
  /** User-facing explanation of what breaks without this dep. */
  reason: string
  /** True when playground cannot function without it (today: only firebase). */
  required: boolean
}

export function rundotGamePlaygroundPlugin(
  options: RundotGamePlaygroundPluginOptions = {},
): Plugin {
  let projectRoot = process.cwd()
  let isServeCommand = false
  let warningLogged = false
  let missingDeps: MissingDep[] = []
  // Resolved headless key (option → process.env → project .env*); never sent to the browser.
  let headlessKey: string | undefined
  const allowNonRkKey = process.env[ALLOW_NON_RK_KEY_ENV] === '1'
  // Host-header allowlist (hostnames, port-stripped) — populated in configResolved.
  let allowedHostnames: string[] = ['localhost', '127.0.0.1', '::1']
  // True when the server binds all interfaces (0.0.0.0/::) — relaxes the Host
  // allowlist so LAN clients aren't 403'd (CSRF check still applies).
  let bindsAllInterfaces = false
  // Resolved bind host (populated in configResolved) — undefined/empty means
  // loopback (Vite's default). Used to gate the LAN anonymous-auth fallback.
  let resolvedBindHost: string | undefined

  const target: PlaygroundTarget = options.target ?? 'playground'

  const logWarning = (message: string): void => {
    if (!warningLogged) {
      warningLogged = true
      console.warn(`[RUN:playground] ${message}`)
    }
  }

  return {
    name: 'rundot-game-sandbox',
    apply: 'serve',
    enforce: 'pre', // Run before other plugins to set up optimizeDeps exclusions

    config(config, env) {
      projectRoot = resolveProjectRoot(config)
      isServeCommand = env.command === 'serve'

      // Resolve the playground key from option → process.env → project .env*.
      // Reads ONLY RUNDOT_PLAYGROUND_KEY (not the deploy key RUNDOT_API_KEY), so an
      // exported deploy key can't hijack playground sign-in. loadEnv with the
      // 'RUNDOT_' prefix only reads RUNDOT_* vars, so it cannot widen exposure; the
      // key stays Node-side (never serialized to the browser).
      const fileEnv = loadEnv(env.mode, projectRoot, 'RUNDOT_')
      headlessKey = options.apiKey || process.env.RUNDOT_PLAYGROUND_KEY || fileEnv.RUNDOT_PLAYGROUND_KEY

      if (!isServeCommand || options.disabled) {
        return config
      }

      // Detect missing playground dependencies. Both checks run regardless of
      // outcome so the dev-time overlay can show every missing dep in one place
      // (one combined install command instead of an install-restart-install loop).
      missingDeps = []
      const hasFirebase = checkFirebaseDependency(projectRoot)
      const hasRevenueCat = checkRevenueCatDependency(projectRoot)
      if (!hasFirebase) {
        missingDeps.push({
          packageName: 'firebase',
          versionConstraint: REQUIRED_FIREBASE_VERSION,
          reason: 'Required for playground auth and Firestore. Playground cannot start without it.',
          required: true,
        })
      }
      if (!hasRevenueCat) {
        missingDeps.push({
          packageName: '@revenuecat/purchases-js',
          versionConstraint: REQUIRED_REVENUECAT_VERSION,
          reason: 'Only needed for IAP / subscription methods. Other playground features still work.',
          required: false,
        })
      }
      logMissingDeps(missingDeps)

      if (!hasFirebase) {
        // Firebase missing is fatal: exclude SDK + firebase from pre-bundling
        // so Vite doesn't crash with a cryptic "Could not resolve firebase/app"
        // before the overlay can render. Also exclude RC if missing for the
        // same reason. RC-only-missing is handled in the live branch below.
        return {
          ...config,
          optimizeDeps: {
            ...config?.optimizeDeps,
            exclude: [
              ...(config?.optimizeDeps?.exclude || []),
              '@series-inc/rundot-game-sdk',
              'firebase',
              'firebase/app',
              'firebase/auth',
              'firebase/firestore',
              ...(hasRevenueCat ? [] : ['@revenuecat/purchases-js']),
            ],
          },
        }
      }

      // Firebase is installed - make sure it's pre-bundled so dynamic imports work
      // Also set VITE_APP_MODE to 'live' so apps use their live adapter (which calls SDK)

      // Build playground config to check target environment
      let playgroundConfig: PlaygroundConfig | null = null
      try {
        playgroundConfig = buildPlaygroundConfig(projectRoot, options, headlessKey, allowNonRkKey)
      } catch {
        // Config errors will be shown in transformIndexHtml
      }

      // Set up proxy for non-local targets to avoid CORS issues
      // Browser requests go to /__rundotgameapi/functionName, Vite proxies to Cloud Functions
      const proxyConfig = buildProxyConfig(playgroundConfig)

      return {
        ...config,
        define: {
          ...config?.define,
          'import.meta.env.VITE_APP_MODE': JSON.stringify('live'),
        },
        optimizeDeps: {
          ...config?.optimizeDeps,
          include: [
            ...(config?.optimizeDeps?.include || []),
            '@series-inc/rundot-game-sdk/playground',
            'firebase/app',
            'firebase/auth',
            'firebase/firestore',
          ],
          // When RC is missing, exclude it so Vite stops trying to resolve
          // the transitive dynamic import in HttpIapApi. The overlay nudges
          // the user; if dismissed, only the five IAP methods throw at call time.
          exclude: [
            ...(config?.optimizeDeps?.exclude || []),
            ...(hasRevenueCat ? [] : ['@revenuecat/purchases-js']),
          ],
        },
        server: {
          ...config?.server,
          proxy: {
            ...config?.server?.proxy,
            ...proxyConfig,
          },
        },
      }
    },

    configResolved(resolved) {
      if (!isServeCommand || options.disabled) return

      // Read the RESOLVED host so CLI `--host 0.0.0.0` is caught (config()'s
      // pre-resolve value would miss it). host === true means bind all interfaces.
      const host = typeof resolved.server.host === 'string'
        ? resolved.server.host
        : resolved.server.host === true ? '0.0.0.0' : undefined

      // Fail-fast: a LIVE headless exchange on a network-exposed bind would let any
      // host on the network mint a playground token via /__rundot-headless-auth. Only
      // guard when the exchange would actually be enabled (eligible key + playground
      // target); a non-eligible/ignored key exposes no oracle, so don't block a
      // legitimate `vite --host`.
      if (shouldExposeHeadlessAuth(headlessKey, target, allowNonRkKey) && !isLoopbackHost(host)
          && process.env[ALLOW_INSECURE_KEY_BIND_ENV] !== '1') {
        throw new Error(
          `[RUN:playground] Refusing to start: headless key + non-loopback host "${host}". ` +
          `Bind to localhost, unset the key, or set ${ALLOW_INSECURE_KEY_BIND_ENV}=1 to override.`,
        )
      }

      // Build the Host-header allowlist (hostnames only; the CSRF custom-header
      // check is the per-request defense, the Host check stops DNS-rebinding).
      const hostnames = new Set(['localhost', '127.0.0.1', '::1'])
      if (typeof host === 'string' && host && !isLoopbackHost(host) && host !== '0.0.0.0') {
        hostnames.add(host.toLowerCase())
      }
      allowedHostnames = [...hostnames]
      // Binding all interfaces is an intentional network exposure (LAN/mobile
      // testing); LAN clients send a non-loopback Host, so relax the rebinding
      // allowlist. The headless-key + non-loopback fail-fast above still applies.
      bindsAllInterfaces = host === '0.0.0.0' || host === '::'
      resolvedBindHost = host

      // LAN/mobile testing hint: Google OAuth can't run from a non-loopback
      // origin (Firebase authorized-domains), so on a LAN bind the SDK falls
      // back to anonymous sign-in (see shouldAllowLanAnonymousAuth). Tell the
      // developer why they're anonymous and how to get their real profile.
      if (shouldAllowLanAnonymousAuth({ isServe: isServeCommand, target, bindHost: host, hasHeadlessAuth: shouldExposeHeadlessAuth(headlessKey, target, allowNonRkKey) })) {
        console.info(
          `[RUN:playground] LAN bind detected ("${host}") — the primary tab will sign in ` +
          `ANONYMOUSLY on phones/other devices (Google sign-in needs a localhost origin). ` +
          `For your real creator profile, load http://localhost from that device instead ` +
          `(e.g. Android: \`adb reverse tcp:<port> tcp:<port>\`).`,
        )
      }
    },

    configureServer(server) {
      server.middlewares.use(async (req, res, next) => {
        // Guard every /__rundot-* dev-server endpoint against DNS-rebinding
        // (Host allowlist) AND cross-origin CSRF (default-deny: require a signal
        // a simple cross-origin request cannot forge). The SDK's own fetches send
        // X-Rundot-Playground: 1; same-origin requests also carry Sec-Fetch-Site.
        if (req.url && isProtectedRundotPath(req.url)) {
          const allowed = isPlaygroundRequestAllowed({
            host: req.headers.host,
            allowedHostnames,
            allowAnyHost: bindsAllInterfaces,
            playgroundHeader: req.headers[PLAYGROUND_REQUEST_HEADER],
            secFetchSite: req.headers['sec-fetch-site'],
          })
          if (!allowed) {
            res.writeHead(403, { 'Content-Type': 'application/json' })
            res.end(JSON.stringify({ error: 'Forbidden' }))
            return
          }
        }

        if (req.url === RUNDOT_HEADLESS_AUTH_PATH && req.method === 'POST') {
          await handleHeadlessAuth(req, res, headlessKey, target, allowNonRkKey)
          return
        }

        if (req.url?.startsWith(PLAYGROUND_INIT_PATH)) {
          const result = await server.transformRequest(PLAYGROUND_INIT_PATH)
          res.setHeader('Content-Type', 'application/javascript')
          res.end(result?.code ?? '')
          return
        }

        // Handle game config write requests from the browser (playground auto-create flow)
        if (req.url === RUNDOT_WRITE_GAME_CONFIG_PATH && req.method === 'POST') {
          let body = ''
          req.on('data', (chunk: Buffer) => { body += chunk.toString() })
          req.on('end', () => {
            try {
              const { gameId, target: rawTarget } = JSON.parse(body) as { gameId: string; target: string }
              if (!isSafeGameId(gameId)) {
                res.writeHead(400, { 'Content-Type': 'application/json' })
                res.end(JSON.stringify({ error: 'Invalid gameId' }))
                return
              }
              const writeTarget = rawTarget ?? 'playground'
              // Strict allowlist — blocks `../` traversal that would let a request
              // write a game.config.<target>.json outside the project root.
              if (!isSafeConfigTarget(writeTarget)) {
                res.writeHead(400, { 'Content-Type': 'application/json' })
                res.end(JSON.stringify({ error: 'Invalid target' }))
                return
              }
              const configFilename = GAME_CONFIG_ENV_FILENAME(writeTarget)
              // Writes must land where reads look: reads walk up the tree, so in
              // a monorepo an existing repo-root config is overwritten in place
              // rather than shadowed by a new file at the Vite root.
              const configPath = resolveGameConfigWritePath(projectRoot, configFilename)
              writeFileSync(configPath, JSON.stringify({ gameId }, null, 2) + '\n', 'utf-8')
              res.writeHead(200, { 'Content-Type': 'application/json' })
              res.end(JSON.stringify({ ok: true }))
            } catch (err) {
              const message = err instanceof Error ? err.message : String(err)
              console.error(`[RUN:playground] Failed to write game config:`, message)
              res.writeHead(500, { 'Content-Type': 'application/json' })
              res.end(JSON.stringify({ error: message }))
            }
          })
          return
        }

        // Serve merged server config (simulation) to the browser
        if (req.url === RUNDOT_SERVER_CONFIG_PATH && req.method === 'GET') {
          const target = options.target ?? 'playground'
          const mergeResult = mergeGameConfigs(projectRoot, target)

          if (mergeResult.errors.length > 0) {
            res.writeHead(400, { 'Content-Type': 'application/json' })
            res.end(JSON.stringify({ errors: mergeResult.errors }))
            return
          }

          if (!mergeResult.config) {
            res.writeHead(404, { 'Content-Type': 'application/json' })
            res.end(JSON.stringify({ error: 'No server config files found' }))
            return
          }

          // Compute hash of merged config — use compact JSON (no spaces/newlines)
          // so formatting differences don't produce different hashes
          const configJson = JSON.stringify(mergeResult.config)
          const hash = createHash('sha256').update(configJson).digest('hex')
          let storedHash: string | undefined
          const gameConfigPath = findGameConfigPath(projectRoot, target)
          if (gameConfigPath) {
            try {
              const raw = readFileSync(gameConfigPath, 'utf-8')
              const parsed = JSON.parse(raw) as { serverConfigHash?: string }
              storedHash = parsed.serverConfigHash
            } catch {
              // Ignore read errors
            }
          }

          const changed = hash !== storedHash

          res.writeHead(200, { 'Content-Type': 'application/json' })
          res.end(JSON.stringify({ config: mergeResult.config, hash, changed }))
          return
        }

        // Persist server config hash after successful upload
        if (req.url === RUNDOT_SERVER_CONFIG_HASH_PATH && req.method === 'POST') {
          let body = ''
          req.on('data', (chunk: Buffer) => { body += chunk.toString() })
          req.on('end', () => {
            try {
              const { hash } = JSON.parse(body) as { hash: string }
              if (!hash || typeof hash !== 'string') {
                res.writeHead(400, { 'Content-Type': 'application/json' })
                res.end(JSON.stringify({ error: 'Missing hash' }))
                return
              }
              const target = options.target ?? 'playground'
              const configFilename = GAME_CONFIG_ENV_FILENAME(target)
              const configPath = findGameConfigPath(projectRoot, target) || join(projectRoot, configFilename)
              let existing: Record<string, unknown> = {}
              try {
                existing = JSON.parse(readFileSync(configPath, 'utf-8')) as Record<string, unknown>
              } catch {
                // File doesn't exist yet or is invalid
              }
              existing.serverConfigHash = hash
              writeFileSync(configPath, JSON.stringify(existing, null, 2) + '\n', 'utf-8')
              res.writeHead(200, { 'Content-Type': 'application/json' })
              res.end(JSON.stringify({ ok: true }))
            } catch (err) {
              const message = err instanceof Error ? err.message : String(err)
              res.writeHead(500, { 'Content-Type': 'application/json' })
              res.end(JSON.stringify({ error: message }))
            }
          })
          return
        }

        next()
      })

      // Watch config files for changes and notify the browser via HMR
      const target = options.target ?? 'playground'
      const baseConfigGlobs = getBaseConfigCandidatesForTarget(target)
        .map(f => join(projectRoot, f).replace(/\\/g, '/'))
      const configGlob = join(projectRoot, 'config', '*.config.json').replace(/\\/g, '/')
      // Watch BOTH the canonical `rundot/` and legacy `.rundot/` folders — the
      // change filter (isServerConfigChange) already accepts both, but the watcher
      // must actually be told to watch canonical `rundot/*.config.json` files.
      const rundotGlobs = RUNDOT_LOOKUP_ORDER.flatMap((dir) => [
        join(projectRoot, dir, '*.config.json').replace(/\\/g, '/'),
        join(projectRoot, dir, 'simulation', '*.json').replace(/\\/g, '/'),
      ])

      const filesToWatch = [...baseConfigGlobs, configGlob, ...rundotGlobs]
      let debounceTimer: ReturnType<typeof setTimeout> | null = null

      for (const pattern of filesToWatch) {
        server.watcher.add(pattern)
      }

      server.watcher.on('change', (changedPath) => {
        if (!isServerConfigChange(changedPath, projectRoot, baseConfigGlobs)) return

        if (debounceTimer) clearTimeout(debounceTimer)
        debounceTimer = setTimeout(() => {
          server.ws.send({ type: 'custom', event: 'rundot-server-config-changed' })
        }, 300)
      })
    },

    resolveId(id) {
      if (id === PLAYGROUND_INIT_PATH) {
        return id
      }
    },

    load(id) {
      if (id === PLAYGROUND_INIT_PATH) {
        // Bridge HMR custom events to window CustomEvents so the pre-bundled SDK can receive them.
        // import.meta.hot is available here because virtual modules are not pre-bundled.
        return [
          `import '@series-inc/rundot-game-sdk/playground';`,
          `if (import.meta.hot) {`,
          `  import.meta.hot.on('rundot-server-config-changed', () => {`,
          `    window.dispatchEvent(new CustomEvent('rundot-hmr-server-config-changed'));`,
          `  });`,
          `}`,
        ].join('\n')
      }
    },

    async transformIndexHtml(html): Promise<IndexHtmlTransformResult> {
      if (!isServeCommand || options.disabled) {
        return html
      }

      const target = options.target ?? 'playground'

      // The missing-deps overlay is rendered by the toolbar script. We just
      // hand it the list via a window global, and inject the toolbar tags
      // (the script is self-contained, so it works even when Firebase is
      // missing and the SDK itself never loads).
      const missingDepsScript = buildMissingDepsConfigScript(missingDeps, target)

      // Firebase missing is fatal — the SDK can't load. Show only the toolbar
      // (which renders the overlay from the global) and skip the shell,
      // playground config, and SDK init since none of it would work.
      if (missingDeps.some((d) => d.required)) {
        return {
          html,
          tags: [
            missingDepsScript,
            ...createPlaygroundToolbarTags(),
          ],
        }
      }

      try {
        const config = buildPlaygroundConfig(projectRoot, options, headlessKey, allowNonRkKey)
        if (!config) {
          return {
            html,
            tags: [
              missingDepsScript,
              ...createPlaygroundToolbarTags(),
            ],
          }
        }

        // Server-gated: allow anonymous sign-in only when this is a LAN-bound
        // dev server on the playground target with no headless key. Never set on
        // a production build, so the hosted playground keeps Google mandatory.
        config.allowLanAnonymousAuth = shouldAllowLanAnonymousAuth({
          isServe: isServeCommand,
          target: config.target,
          bindHost: resolvedBindHost,
          hasHeadlessAuth: shouldExposeHeadlessAuth(headlessKey, config.target, allowNonRkKey),
        })

        const injectedHtml = injectRundotGameShell(html)
        const configScript = `window.__RUNDOT_GAME_PLAYGROUND__ = ${JSON.stringify(config)};`

        // Log server config merge results regardless of whether a config was
        // produced. A malformed/double-wrapped file yields errors with NO config
        // (so hasServerConfig stays unset) — gating on it would swallow exactly the
        // failures we most need to surface (e.g. a deterministic room declared the
        // documented way but rejected as a double-wrap → the silent "No server
        // bundle found" trap).
        const mergeResult = mergeGameConfigs(projectRoot, config.target)
        for (const err of mergeResult.errors) {
          console.error(`[RUN:playground] Config error: ${err}`)
        }
        for (const warn of mergeResult.warnings) {
          console.warn(`[RUN:playground] Config warning: ${warn}`)
        }

        const revenueCatImportMap = buildRevenueCatImportMapTag(projectRoot)

        return {
          html: injectedHtml,
          tags: [
            ...(revenueCatImportMap ? [revenueCatImportMap] : []),
            {
              tag: 'script',
              attrs: { type: 'module' },
              children: configScript,
              injectTo: 'head',
            },
            missingDepsScript,
            {
              tag: 'script',
              attrs: { type: 'module', src: PLAYGROUND_INIT_PATH },
              injectTo: 'head',
            },
            ...createPlaygroundToolbarTags(),
          ],
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error)
        logWarning(message)

        // Playground config error overlay (separate path — distinct from missing
        // deps). Still surface any missing-dep overlay alongside.
        const errorScript = buildErrorOverlayScript(message, target)
        return {
          html,
          tags: [
            missingDepsScript,
            ...createPlaygroundToolbarTags(),
            {
              tag: 'script',
              attrs: { type: 'module' },
              children: errorScript,
              injectTo: 'body',
            },
          ],
        }
      }
    },
  }
}

/**
 * Build the playground config from project files.
 */
function buildPlaygroundConfig(
  projectRoot: string,
  options: RundotGamePlaygroundPluginOptions,
  headlessKey?: string,
  allowNonRkKey = false,
): PlaygroundConfig | null {
  const target = options.target ?? 'playground'

  // Get gameId from options or game.config.{env}.json
  let gameId = options.gameId?.trim()

  if (!gameId) {
    const gameConfigPath = findGameConfigPath(projectRoot, target)
    if (gameConfigPath) {
      const gameConfig = readGameConfig(gameConfigPath)
      gameId = gameConfig.gameId?.trim()
    }
  }

  // For playground: allow starting without a gameId — the game will be auto-created
  // after the user logs in via the toolbar (no CLI dependency).
  // For other targets: gameId is still required upfront.
  if (!gameId && target !== 'playground') {
    throw new Error(
      `[RUN:playground] Missing gameId. Either:\n` +
      `  1. Add gameId to vite.config: rundotGamePlaygroundPlugin({ gameId: 'your-app-id' })\n` +
      `  2. Create game.config.${target}.json with { "gameId": "your-app-id" }\n` +
      `  3. Run "rundot configure-game" if using RUN.world CLI`
    )
  }

  // Build the config
  const firebaseConfig = FIREBASE_CONFIGS[target]
  const config: PlaygroundConfig = {
    enabled: true,
    target,
    gameId: gameId || '',
    firebaseConfig,
    functionsRegion: FUNCTIONS_REGIONS[target],
    pollingInterval: 2000,
    rpcMaxRetries: 0,
    rpcRetryDelayMs: 1000,
    sdkVersion: SDK_VERSION,
  }

  // Display name for the auto-created playground game (used when no gameId
  // exists yet): the project-root package.json name, else the directory name.
  config.suggestedGameName = resolveSuggestedGameName(projectRoot)

  // Inject Game Manager proxy URL for playground (used to auto-create games)
  if (GAME_MANAGER_URLS[target]) {
    config.gameManagerUrl = RUNDOT_GAME_MANAGER_PROXY_PATH
  }

  // Add emulator hosts for local development
  if (target === 'local') {
    config.functionsEmulatorHost = DEFAULT_EMULATOR_HOSTS.functions
    config.firestoreEmulatorHost = DEFAULT_EMULATOR_HOSTS.firestore
    config.authEmulatorHost = DEFAULT_EMULATOR_HOSTS.auth
  }

  // Add Cloud Run URL for UGC and other Cloud Run services
  // All modes use Vite proxy path to avoid CORS issues (the emulator's CORS
  // handling is unreliable in embedded browsers like Cursor's Simple Browser).
  config.cloudRunUrl = RUNDOT_GAME_CLOUD_RUN_PROXY_PATH

  // Hosted multiplayer room server (R9(b)). Default ON for the `playground`
  // target (its per-target URL) — no game runs hosted MP today, and playground is
  // meant to mirror prod. Opt OUT (fall back to the local dev sidecar) with
  // RUNDOT_PLAYGROUND_ROOM_SERVER_URL=0|false|local; or pass an explicit URL to
  // override. Other targets have no hosted URL here, so they keep the sidecar.
  // Unlike cloudRunUrl this is the REAL host, not a proxy path: WsMultiplayerApi
  // uses it for both the `ws://` connection and the `/tickets` POST. A
  // deterministic (Syncplay) room needs NO deployed bundle — the room-server
  // resolves it from the uploaded server config (servers/{serverId}.value.rooms),
  // which `npm run dev` publishes. Non-deterministic creator rooms still need a deploy.
  const roomServerSetting = process.env.RUNDOT_PLAYGROUND_ROOM_SERVER_URL?.trim()
  const roomServerDisabled =
    roomServerSetting === '0' || roomServerSetting === 'false' || roomServerSetting === 'local'
  if (!roomServerDisabled) {
    const roomServerUrl =
      roomServerSetting && /^https?:\/\//.test(roomServerSetting)
        ? roomServerSetting
        : ROOM_SERVER_URLS[target]
    if (roomServerUrl) {
      config.roomServerUrl = roomServerUrl
    }
  }

  // RevenueCat Web Billing public key — env var wins over the per-target
  // default so projects can override locally without editing firebaseConfigs.ts.
  const rcKey = process.env.RUNDOT_REVENUECAT_WEB_BILLING_KEY || REVENUECAT_WEB_BILLING_KEYS[target]
  if (rcKey) {
    config.revenueCatPublicApiKey = rcKey
  }

  // Detect server config files (simulation) and set versionTag for all targets
  const mergeResult = mergeGameConfigs(projectRoot, target)
  if (mergeResult.config) {
    config.hasServerConfig = true
    config.versionTag = 'private'
  }

  // Signal (NOT the key) that headless auth is available. The raw key never
  // reaches the browser — the SDK fetches a custom token from the
  // /__rundot-headless-auth dev-server middleware, which does the exchange
  // Node-side. Only a `pk_` (playground) key on playground qualifies; a non-`pk_`
  // (internal QA) key needs the explicit opt-in.
  if (shouldExposeHeadlessAuth(headlessKey, target, allowNonRkKey)) {
    config.hasHeadlessAuth = true
    console.warn('[RUN:playground] Headless key mode enabled — keep this dev server bound to localhost; do NOT expose it publicly')
    console.warn('[RUN:playground] This pk_ key is your identity (minted from a prod game you edit). The playground game being run is the id in game.config.playground.json — the two ids intentionally do not match.')
  } else if (headlessKey && headlessKey.startsWith('rk_') && !allowNonRkKey) {
    console.warn('[RUN:playground] Ignoring an rk_ deploy key in RUNDOT_PLAYGROUND_KEY — run `rundot playground grant-access` to mint a playground (pk_) key.')
  } else if (headlessKey && !headlessKey.startsWith('pk_') && !allowNonRkKey) {
    console.warn(`[RUN:playground] Ignoring non-pk_ headless key. Use a playground (pk_) key from \`rundot playground grant-access\`, or set ${ALLOW_NON_RK_KEY_ENV}=1 for internal QA.`)
  }

  if (options.appRole) {
    config.appRole = options.appRole
  }

  return config
}

/**
 * Read a game config file.
 */
function readGameConfig(pathname: string): { gameId?: string } {
  try {
    const raw = readFileSync(pathname, 'utf-8')
    return JSON.parse(raw) as { gameId?: string }
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error'
    throw new Error(`Failed to parse ${pathname}: ${message}`)
  }
}

/**
 * Find game config by walking up the directory tree.
 * Looks for game.config.{env}.json only — the legacy game.config.json fallback
 * has been removed because nothing creates that file anymore (CLI writes
 * env-specific files, playground auto-create writes game.config.playground.json).
 * The fallback caused cross-environment gameId mismatches when a stale
 * game.config.json contained a gameId from a different environment.
 */
function findGameConfigPath(startDir: string, target?: PlaygroundTarget): string | null {
  if (!target) return null
  return findUpGameConfigPath(startDir, GAME_CONFIG_ENV_FILENAME(target))
}

/**
 * Resolve project root from Vite config or CWD.
 */
function resolveProjectRoot(config?: { root?: string }): string {
  if (config?.root && config.root.length > 0) {
    return resolvePath(config.root)
  }
  return process.cwd()
}

/**
 * Inject RUN.world shell wrapper around body content.
 */
function injectRundotGameShell(html: string): string {
  const match = html.match(/<body([^>]*)>([\s\S]*)<\/body>/i)
  if (!match) {
    return html
  }

  const [, attrs, contents] = match
  const serialized = JSON.stringify(contents).replace(/<\/script>/gi, '<\\/script>')

  const shell = `
    <body${attrs}>
      <div id="rundot-game-toolbar-root" aria-live="polite"></div>
      <div id="rundot-game-app-root" data-rundot-game-app-root style="display:contents;">
        ${contents}
      </div>
      <noscript id="rundot-game-app-placeholder">
        RUN.world playground requires JavaScript enabled.
      </noscript>
      <script id="rundot-game-app-capture">
        window.__RUNDOT_GAME_APP_HTML = ${serialized};
      </script>
    </body>
  `

  return html.replace(/<body([\s\S]*)<\/body>/i, shell)
}

/**
 * Returns true when the package is declared in the project's own
 * `package.json` (in `dependencies`, `devDependencies`, `peerDependencies`,
 * or `optionalDependencies`).
 *
 * We check the declaration instead of resolving from node_modules because
 * Node's resolver walks up the directory tree. In a monorepo with npm/pnpm
 * workspaces, a creator's game might intentionally rely on a dep hoisted to
 * the workspace root — a resolve-based check would still find it, but a
 * path-based "must live under projectRoot/node_modules" check would wrongly
 * flag it as missing. Conversely, an unrelated sibling's node_modules — e.g.
 * `packages/sdk/node_modules/firebase` left over from the SDK's own
 * devDependencies — would resolve successfully even when the creator hasn't
 * declared the dep. The package.json declaration is the only signal that
 * matches the creator's intent.
 *
 * Trade-off: if the creator declared the dep but never ran `npm install`,
 * this returns true and we skip the overlay. They'd then see the normal
 * Vite "Failed to resolve" error on the first import — which is the existing
 * behavior outside this plugin, so we're not making it worse.
 */
function isDeclaredDependency(projectRoot: string, packageName: string): boolean {
  try {
    const pkgPath = join(projectRoot, 'package.json')
    const pkg = JSON.parse(readFileSync(pkgPath, 'utf-8')) as {
      dependencies?: Record<string, string>
      devDependencies?: Record<string, string>
      peerDependencies?: Record<string, string>
      optionalDependencies?: Record<string, string>
    }
    return Boolean(
      pkg.dependencies?.[packageName]
      ?? pkg.devDependencies?.[packageName]
      ?? pkg.peerDependencies?.[packageName]
      ?? pkg.optionalDependencies?.[packageName],
    )
  } catch {
    return false
  }
}

function checkFirebaseDependency(projectRoot: string): boolean {
  return isDeclaredDependency(projectRoot, 'firebase')
}

function checkRevenueCatDependency(projectRoot: string): boolean {
  return isDeclaredDependency(projectRoot, '@revenuecat/purchases-js')
}

/**
 * Log a unified missing-deps message to console. Mirrors the browser overlay:
 * one block listing every missing playground dep with a single combined
 * `npm install --save-dev` command. Uses `console.error` when any required
 * dep is missing, otherwise `console.warn`.
 */
function logMissingDeps(deps: MissingDep[]): void {
  if (deps.length === 0) return
  const hasRequired = deps.some((d) => d.required)
  const installArgs = deps.map((d) => `${d.packageName}@${d.versionConstraint}`).join(' ')
  const lines = deps.map((d) => {
    const tag = d.required ? 'REQUIRED' : 'OPTIONAL'
    return `  • [${tag}] ${d.packageName}@${d.versionConstraint}\n      ${d.reason}`
  })
  const message = `
[RUN:playground] Missing dependencies — playground might not work properly.

${lines.join('\n')}

Install command:

  npm install --save-dev ${installArgs}

These deps are only used during local development (playground mode) and are
excluded from production builds, so install them as devDependencies.
`
  if (hasRequired) {
    console.error(message)
  } else {
    console.warn(message)
  }
}

/**
 * Build the inline script tag that hands missing-deps info to the toolbar
 * script. The toolbar script reads `window.__RUNDOT_GAME_PLAYGROUND_MISSING_DEPS__`
 * on init and renders the overlay UI itself (see playgroundToolbarScript.ts).
 *
 * Always emitted — when there are no missing deps the global is `null` and
 * the toolbar skips the overlay path. Doing it this way keeps the HTML/CSS
 * out of this Node-side file.
 */
function buildMissingDepsConfigScript(deps: MissingDep[], target: PlaygroundTarget): HtmlTagDescriptor {
  const payload = deps.length === 0
    ? null
    : {
        deps: deps.map((d) => ({
          packageName: d.packageName,
          versionConstraint: d.versionConstraint,
          reason: d.reason,
          required: d.required,
        })),
        installCommand: `npm install --save-dev ${deps.map((d) => `${d.packageName}@${d.versionConstraint}`).join(' ')}`,
        target,
      }
  return {
    tag: 'script',
    attrs: { type: 'module' },
    children: `window.__RUNDOT_GAME_PLAYGROUND_MISSING_DEPS__ = ${JSON.stringify(payload)};`,
    injectTo: 'head',
  }
}

/**
 * Build error overlay script for display in browser.
 */
function buildErrorOverlayScript(message: string, target: PlaygroundTarget): string {
  const escapedMessage = message
    .replace(/\\/g, '\\\\')
    .replace(/'/g, "\\'")
    .replace(/\n/g, '\\n')

  return `
(function() {
  var overlay = document.createElement('div');
  overlay.id = 'rundot-game-sandbox-error';
  overlay.style.cssText = 'position:fixed;inset:0;z-index:999999;background:rgba(0,0,0,0.92);color:#fff;font-family:-apple-system,BlinkMacSystemFont,sans-serif;display:flex;align-items:center;justify-content:center;padding:20px;';

  var content = document.createElement('div');
  content.style.cssText = 'max-width:600px;text-align:center;';

  var icon = document.createElement('div');
  icon.style.cssText = 'font-size:48px;margin-bottom:16px;';
  icon.textContent = '⚠️';

  var title = document.createElement('h1');
  title.style.cssText = 'font-size:24px;font-weight:600;margin:0 0 16px;color:#ff6b6b;';
  title.textContent = 'RUN.world Playground Error';

  var msg = document.createElement('p');
  msg.style.cssText = 'font-size:14px;line-height:1.6;margin:0 0 24px;color:#ccc;';
  msg.textContent = '${escapedMessage}';

  var hint = document.createElement('div');
  hint.style.cssText = 'background:rgba(255,255,255,0.1);border-radius:8px;padding:16px;font-size:13px;color:#aaa;';
  hint.innerHTML = '<strong style="color:#fff;">Environment:</strong> ${target}<br>Check your game.config.${target}.json and vite.config plugin options';

  content.appendChild(icon);
  content.appendChild(title);
  content.appendChild(msg);
  content.appendChild(hint);
  overlay.appendChild(content);

  if (document.body) {
    document.body.appendChild(overlay);
  } else {
    document.addEventListener('DOMContentLoaded', function() {
      document.body.appendChild(overlay);
    });
  }
})();
`
}

/**
 * The proxy path used by the SDK to route requests through Vite.
 * This avoids CORS issues when calling Cloud Functions from localhost.
 */
export const RUNDOT_GAME_API_PROXY_PATH = '/__rundotgameapi'

/**
 * Proxy path for Cloud Run requests (UGC and other Cloud Run services).
 * This avoids CORS issues when calling Cloud Run from localhost.
 */
export const RUNDOT_GAME_CLOUD_RUN_PROXY_PATH = '/__rundotcloudrun'

/**
 * Proxy path for Game Manager requests (auto-create games in playground).
 * This avoids CORS issues when calling the Game Manager service from localhost.
 */
export const RUNDOT_GAME_MANAGER_PROXY_PATH = '/__rundotgamemanager'

/**
 * Middleware path for writing game config files from the browser.
 * Used by the playground auto-create flow to persist the new gameId.
 */
export const RUNDOT_WRITE_GAME_CONFIG_PATH = '/__rundot-write-game-config'

/**
 * Middleware path for fetching merged server config (simulation) from the browser.
 * The Vite plugin merges config files on the Node side and serves the result.
 */
export const RUNDOT_SERVER_CONFIG_PATH = '/__rundot-server-config'

/**
 * Middleware path for persisting the server config hash after successful upload.
 * Called by PlaygroundHost after uploading to VGM so subsequent loads can skip redundant uploads.
 */
export const RUNDOT_SERVER_CONFIG_HASH_PATH = '/__rundot-server-config-hash'

export { RUNDOT_HEADLESS_AUTH_PATH, handleHeadlessAuth, clearHeadlessAuthFailureCache } from './headlessAuth'



/**
 * Map playground target to ordered base config filename candidates (mirrors configMerger logic).
 * Falls back to config.json when the target-specific file doesn't exist.
 */
function getBaseConfigCandidatesForTarget(target: PlaygroundTarget): string[] {
  switch (target) {
    case 'local':
      return ['config.local.json', 'config.json']
    case 'dev':
    case 'staging':
      return ['config.staging.json', 'config.json']
    case 'playground':
    default:
      return ['config.json']
  }
}

/**
 * Build Vite proxy configuration for non-local playground targets.
 *
 * For dev/staging, Cloud Functions don't allow localhost origins.
 * We proxy through Vite to avoid CORS issues:
 * - Browser requests: /__rundotgameapi/functionName
 * - Vite proxies to: https://us-central1-{project}.cloudfunctions.net/functionName
 */
function buildProxyConfig(
  config: PlaygroundConfig | null,
): Record<string, Record<string, unknown>> {
  if (!config) {
    return {}
  }

  // Build the Cloud Functions target URL
  const projectId = config.firebaseConfig?.projectId
  const region = config.functionsRegion || 'us-central1'

  if (!projectId) {
    return {}
  }

  // For local: proxy to the Firebase Functions emulator
  // For dev/staging: proxy to deployed Cloud Functions
  // All modes use Vite proxy to avoid CORS issues (the emulator's CORS handling
  // is unreliable in embedded browsers like Cursor's Simple Browser).
  const functionsTargetUrl = config.target === 'local'
    ? `http://${config.functionsEmulatorHost || DEFAULT_EMULATOR_HOSTS.functions}`
    : `https://${region}-${projectId}.cloudfunctions.net`

  // For local emulator, the URL format includes projectId/region prefix
  const functionsRewrite = config.target === 'local'
    ? (path: string) => `/${projectId}/${region}${path.replace(new RegExp(`^${RUNDOT_GAME_API_PROXY_PATH}`), '')}`
    : (path: string) => path.replace(new RegExp(`^${RUNDOT_GAME_API_PROXY_PATH}`), '')

  const proxyConfig: Record<string, Record<string, unknown>> = {
    [RUNDOT_GAME_API_PROXY_PATH]: {
      target: functionsTargetUrl,
      changeOrigin: true,
      rewrite: functionsRewrite,
    },
  }

  // Add Cloud Run proxy for all targets
  const cloudRunUrl = CLOUD_RUN_URLS[config.target]
  if (cloudRunUrl) {
    proxyConfig[RUNDOT_GAME_CLOUD_RUN_PROXY_PATH] = {
      target: cloudRunUrl,
      changeOrigin: true,
      rewrite: (path: string) => path.replace(new RegExp(`^${RUNDOT_GAME_CLOUD_RUN_PROXY_PATH}`), ''),
      // Strip Origin header to bypass CORS check (server allows no-origin requests)
      configure: (proxy: { on: (event: string, handler: (proxyReq: { removeHeader: (name: string) => void }) => void) => void }) => {
        proxy.on('proxyReq', (proxyReq) => {
          proxyReq.removeHeader('origin')
        })
      },
    }
  }

  // Add Game Manager proxy for playground (auto-create games)
  const gameManagerUrl = GAME_MANAGER_URLS[config.target]
  if (gameManagerUrl) {
    proxyConfig[RUNDOT_GAME_MANAGER_PROXY_PATH] = {
      target: gameManagerUrl,
      changeOrigin: true,
      rewrite: (path: string) => path.replace(new RegExp(`^${RUNDOT_GAME_MANAGER_PROXY_PATH}`), ''),
      configure: (proxy: { on: (event: string, handler: (proxyReq: { removeHeader: (name: string) => void }) => void) => void }) => {
        proxy.on('proxyReq', (proxyReq) => {
          proxyReq.removeHeader('origin')
        })
      },
    }
  }

  return proxyConfig
}
